import Feature from 'ol/Feature.js';
import { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT.js';
import WMSCapabilities from 'ol/format/WMSCapabilities.js';
import Point from 'ol/geom/Point.js';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj.js';
import { get as getProjection } from 'ol/proj';
import { getWidth } from 'ol/extent';
import { ImageStatic, ImageWMS, XYZ } from 'ol/source';
import TileWMS from 'ol/source/TileWMS';
import TileGrid from 'ol/tilegrid/TileGrid.js';
import { WMSTileSize } from '../../components/sitebase/Base.types';
import VectorSource from 'ol/source/Vector';
import VectorTileSource from 'ol/source/VectorTile.js';
import { Icon } from 'ol/style.js';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import React from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { EFAS_WMSGetCapabilitiesI, FeatureInfoStore, FeatureInfoStoreData, GetFeatureInfoData, GLOFAS_WMSGetCapabilitiesI, LegendData, MapActiveLayer, WMSGetCapabilitiesDetail, WMSInfo, WMSLayer, WMSLayerDimension, WMSMap } from './Map.types';
import { epsg3857 } from './maputils.ts';
import markerIconFilled from './static/marker-icon-filled.png';
import IS_EFAS from '../../components/sitebase/Environment.ts';
import MapBrowserEvent from 'ol/MapBrowserEvent';

import './css/map.css';

import formalIcon from './static/formal.png';
import informalIcon from './static/informal.png';
import flashFloodIcon from './static/flashflood.png';

import { GfmAoiService } from './services/GfmAoiService';
import { MissedEventsService } from './services/MissedEventsService';
import { FeedbacksService } from './services/FeedbacksService';
import { InteractivePopupService } from './services/InteractivePopupService';
import ImageLayer from 'ol/layer/Image';
import moment from 'moment';
import type { AlertPoint } from '../efas/notifications/Notification.types';

export const LAYER_ENCODE_SEPARATOR = "$"

/**
 * Build a custom OL TileGrid for a given tile size, or return undefined for the default grid.
 * https://openlayers.org/en/latest/examples/wms-custom-tilegrid-512x256.html
 * and https://github.com/ecmwf/glofas-web/blob/77817bf2a0290d307fa75242d30531acd97fb12d/frontend/reactjs/glofasmap/utils/openlayers/Ol3Utils.js
 */
export function buildTileGrid(tileSize?: WMSTileSize): TileGrid | undefined {
    const tileSizePx = tileSize === '256' ? 256 : tileSize === '512' ? 512 : null;
    if (tileSizePx === null) return undefined;
    const projExtent = getProjection('EPSG:3857')!.getExtent();
    const startResolution = getWidth(projExtent) / (tileSizePx * 2);
    const resolutions = new Array(22);
    for (let i = 0; i < resolutions.length; ++i) {
        resolutions[i] = startResolution / Math.pow(2, i);
    }
    return new TileGrid({
        extent: projExtent,
        resolutions: resolutions,
        tileSize: [tileSizePx, tileSizePx],
    });
}

export class MapPresenter {

    // State that must be in sync with the Map view
    layers: MapActiveLayer[] = [];
    featureInfos: FeatureInfoStore = {};
    wms: WMSMap = {};
    useMapMarkers: boolean = true;
    forceMapserver: boolean = false;

    ///////////////////////////////////////////////

    readonly map: Map

    private legend: LegendData = [];

    // Services for extracted functionality
    readonly gfm: GfmAoiService;
    readonly missedEvents: MissedEventsService;
    readonly feedbacks: FeedbacksService;
    private interactivePopupService: InteractivePopupService;

    // private wmsGroupIcons: { [key: string]: string } = {};
    private baseMapLayer: TileLayer<XYZ>;
    private iconLayer: VectorLayer<VectorSource>;
    private activeNotificationsLayer: VectorLayer<VectorSource>;

    private featureCounter: number = 1;

    // Cache for ImageStatic sources to avoid recreating them for visited steps
    private imageStaticCache = new globalThis.Map<string, ImageStatic>();

    constructor(
        map: Map
    ) {
        this.map = map;

        // Initialize services
        this.gfm = new GfmAoiService(map);
        this.missedEvents = new MissedEventsService(map);
        this.feedbacks = new FeedbacksService(map);
        this.interactivePopupService = new InteractivePopupService();

        this.baseMapLayer = new TileLayer({
            source: new XYZ(
                {
                    url: 'https://gisco-services.ec.europa.eu/maps/tiles/OSMCartoV4CompositeEN/EPSG3857/{z}/{x}/{y}.png',
                    attributions: '© OpenStreetMap contributors, © EuroGeographics for the administrative boundaries, © Eurostat — GISCO'
                }
            ),
            zIndex: -1,
        });
        this.map.addLayer(this.baseMapLayer);

        this.iconLayer = new VectorLayer({
            source: new VectorSource({
                features: [],
            }),
            zIndex: 1000,
        });
        this.map.addLayer(this.iconLayer);
        this.activeNotificationsLayer = new VectorLayer({
            source: new VectorSource({
                features: [],
            }),
            visible: false,
            zIndex: 999,
        });
        this.map.addLayer(this.activeNotificationsLayer);

        this.map.on('pointermove', (e) => {
            if (e.dragging) {
                return;
            }
            const hit = this.map.hasFeatureAtPixel(e.pixel, {
                layerFilter: (layer) => {
                    if (layer === this.iconLayer) return true;
                    if (layer === this.activeNotificationsLayer && this.activeNotificationsLayer.getVisible()) return true;
                    if (layer === this.missedEvents.getMissedEventsLayer() && this.missedEvents.isLayerVisible()) return true;
                    if (layer === this.feedbacks.getFeedbacksLayer() && this.feedbacks.isLayerVisible()) return true;
                    return false;
                },
                hitTolerance: 5,
            });
            this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });
    }

    /**
     * Copies the view's React state onto the presenter, so its methods work from the latest values.
     * Called by the view whenever any of these change.
     */
    syncViewState(state: Pick<MapPresenter, 'layers' | 'featureInfos' | 'wms' | 'useMapMarkers' | 'forceMapserver'>) {
        this.layers = state.layers;
        this.featureInfos = state.featureInfos;
        this.wms = state.wms;
        this.useMapMarkers = state.useMapMarkers;
        this.forceMapserver = state.forceMapserver;
    }

    private hasDimension(layerDims: undefined | WMSLayerDimension | WMSLayerDimension[], dimension: string): boolean {
        if (!layerDims) {
            return false;
        }
        if (layerDims instanceof Array) {
            return layerDims.some(dim => dim['name'].toLowerCase() === dimension.toLowerCase());
        } else {
            return layerDims['name'].toLowerCase() === dimension.toLowerCase();
        }
    }

    isDrawing() {
        return this.gfm.isDrawing() || this.missedEvents.isDrawing();
    }

    parseKeywords(keywords: string | string[]) {
        const keywordDict: { [key: string]: string } = {};

        let kwArray: string[];
        if (keywords instanceof Array) {
            kwArray = keywords;
        } else {
            kwArray = [keywords];
        }

        kwArray.filter(kw => kw?.includes("=")).map(kw => {
            const [name, val] = kw.split("=");
            keywordDict[name] = val;
        });
        return keywordDict;
    }

    _processLayerMetadata(wmsInfo: WMSInfo, layer: WMSLayer, parentGroup?: string) {
        if (layer.Name === wmsInfo.latestForecastLayer) {
            if (!(layer.Dimension instanceof Array) && this.hasDimension(layer.Dimension, "time")) {
                wmsInfo.latestForecastDate = layer.Dimension['default'];
            } else {
                if (layer.Dimension instanceof Array) {
                    // filter out time dimension by names name or name
                    const timeDim = layer.Dimension.find(dim => dim['name'] === 'time');
                    if (timeDim) {
                        wmsInfo.latestForecastDate = timeDim['default'];
                    }
                }
            }
        }
        if (layer.KeywordList) { // the group is stored in the keyword list - like on EFAS
            const keywords = this.parseKeywords(layer.KeywordList);
            layer.Keywords = keywords;
        }

        let group = 'Uncategorized';
        try {
            group = parentGroup ? parentGroup : (layer.Keywords["group"] || layer.KeywordList[0]);
        } catch (e) {
            console.debug("Error parsing group for layer", layer.Name, e);
        }

        if (!wmsInfo.layers[group]) {
            wmsInfo.layers[group] = [layer];
        } else {
            wmsInfo.layers[group].push(layer);
        }

        layer.wmsInfo = wmsInfo;

    }
    processCapabilities(json: EFAS_WMSGetCapabilitiesI | GLOFAS_WMSGetCapabilitiesI | WMSGetCapabilitiesDetail, wmsInfo: WMSInfo): WMSInfo {
        // navigating the different structures can be a bit of a pain
        // this tries to at least centralise the nonsense here and make it
        // easier to understand which structure is being used
        let iterable;
        if ('getcapabilities' in json) { // EFAS WMS
            json = (json as EFAS_WMSGetCapabilitiesI).getcapabilities;
            iterable = json.WMS_Capabilities.Capability.Layer.Layer;
        } else if ('WMS_Capabilities' in json) { // GLOFAS WMS
            json = json as GLOFAS_WMSGetCapabilitiesI;
            iterable = json.WMS_Capabilities.Capability.Layer.Layer;
        } else { // outcome of OpenLayers WMSCapabilities parser
            json = json as WMSGetCapabilitiesDetail;
            iterable = json.Capability.Layer.Layer;
        }

        for (const layer of iterable) {
            if (layer.Layer) {
                console.debug("Layer with layer: ", layer.Layer)
                if (Array.isArray(layer.Layer)) {
                    for (const subLayer of layer.Layer) {
                        this._processLayerMetadata(wmsInfo, subLayer, layer.Name);
                    }
                } else {
                    this._processLayerMetadata(wmsInfo, layer.Layer, layer.Name);
                }
            } else {
                // the group is stored in the keyword list - like on EFAS
                this._processLayerMetadata(wmsInfo, layer);
            }
        }
        return wmsInfo;
    }


    loadCapabilities(order: string, server: {
        name: string,
        url: string,
        params: string,
        proxy?: string,
        latestForecastLayer: string,
        featureInfoFormat: string,
        forecastLayerInfoFormat?: string,
        forecastLayers?: string[],
        capabilitiesFormat: string,
        version: string,
        tileSize?: WMSTileSize,
        gutter?: number
    }, token?: string, forceReload?: boolean) {
        const params = token ? `${server.params}&token=${token}` : server.params;
        const url = server.proxy ? `${server.proxy}${encodeURIComponent(server.url + params)}` : `${server.url}${params}`;
        // once a day after 10AM UTC force a no-cache request to make sure the 12UTC forecast is picked up
        // store whether this has been done in localStorage
        const now = new Date();
        const fetchParams: { [key: string]: string } = {
            method: 'GET',
        };
        const lastFetchDate = localStorage.getItem('lastFetchDate');
        // not the same date
        if (forceReload || !lastFetchDate || new Date(lastFetchDate).getDate() !== now.getDate()) {
            // DISS will check the forecast after
            if (forceReload || now.getUTCHours() >= 10) {
                localStorage.setItem('lastFetchDate', now.toISOString());
                fetchParams['cache'] = 'reload';
            }
        }

        return fetch(url, fetchParams)
            .then((response) => {
                if (response.ok) {
                    if (server.capabilitiesFormat === 'text/xml' || server.capabilitiesFormat === 'text/plain') {
                        return response.text();
                    } else {
                        return response.json();
                    }
                } else {
                    console.error("Error fetching capabilities for ", url, response);
                    return null;
                }
            })
            .then((json: EFAS_WMSGetCapabilitiesI | string) => {
                let capabilities;
                if (typeof json === 'string') {
                    const parser = new WMSCapabilities();
                    capabilities = parser.read(json);

                } else if (json instanceof Object) {
                    capabilities = json;
                }

                const wmsInfo: WMSInfo = {
                    name: server.name,
                    url: server.url,
                    latestForecastLayer: server.latestForecastLayer,
                    latestForecastDate: "",
                    featureInfoFormat: server.featureInfoFormat ? server.featureInfoFormat : "text/html",
                    forecastLayerInfoFormat: server.forecastLayerInfoFormat,
                    forecastLayers: server.forecastLayers,
                    version: server.version,
                    tileSize: server.tileSize,
                    gutter: server.gutter,
                    layers: {},
                    order: parseInt(order)
                };
                return this.processCapabilities(capabilities, wmsInfo);

            });
    }

    getFeatureInfo(event: MapBrowserEvent, forecastDate: string): Promise<FeatureInfoStore> {
        const promises: Promise<GetFeatureInfoData>[] = [];
        const iconUuid = this.featureCounter;

        for (const layerInfo of this.layers) {
            const layer = layerInfo.layer;

            // if the layer has a keyword to give the queryable status use it. Otherwise assume it's queryable
            // as external WMS layers won't have that keyword as it's a CEMS WMS internal
            const queryable = layerInfo.layerInfo.Keywords && layerInfo.layerInfo.Keywords.queryable ? layerInfo.layerInfo.Keywords.queryable : "true";

            if (!layer.getVisible() || queryable !== "true") {
                continue;
            }

            if (layer instanceof VectorLayer) {
                // throw new Error("Vector layers are not supported for feature info yet");
                // console.debug("Map pixel hit: ", this.map.getFeaturesAtPixel(event.pixel))
                // const features = this.map.getFeaturesAtPixel(event.pixel);
                promises.push(
                    // layer.getFeatures(event.pixel).then((features) => {
                    new Promise((resolve) => resolve({
                        id: uuidv4(),
                        url: 'vector',
                        marker: iconUuid,
                        source: layerInfo.source,
                        name: layerInfo.displayName,
                        layerName: layerInfo.name,
                        forecastDate: forecastDate,
                        coordinate: event.coordinate,
                        data: this.map.getFeaturesAtPixel(event.pixel, {
                            layerFilter: (layerCandidate) => layerCandidate === layer,
                            hitTolerance: 5
                        })
                    }))
                    // })
                );
            } else if (layer instanceof VectorTileLayer) {
                // throw new Error("Vector layers are not supported for feature info yet");
                promises.push(layer.getFeatures(event.pixel).then((features) => {
                    console.debug("Features", features)
                    return {
                        id: uuidv4(),
                        url: 'vector',
                        marker: iconUuid,
                        source: layerInfo.source,
                        name: layerInfo.displayName,
                        layerName: layerInfo.name,
                        forecastDate: forecastDate,
                        coordinate: event.coordinate,
                        data: features,
                    };
                }));
            } else if (layer instanceof TileLayer) {
                const extra_dimensions: { [dimensionName: string]: string } = {};
                const layerSource = layer.getSource();

                if (layerInfo.dimensions instanceof Array) {
                    // filter out time and create a dict of additional dimensions to be added to the GetFeatureInfo request
                    for (const dim of layerInfo.dimensions.filter((dim) => dim['name'] !== 'time')) {
                        if (layerSource instanceof TileWMS) {
                            extra_dimensions[dim['name']] = layerSource.getParams()[dim['name']];
                        } else {
                            console.error("Not implemented for this layer type")
                        }
                    }
                }

                let featureInfoFormat = this.wms[layerSource!.urls[0]].featureInfoFormat;
                if (layerInfo.layerInfo.wmsInfo.forecastLayerInfoFormat
                    && layerInfo.layerInfo.wmsInfo.forecastLayers
                    && layerInfo.layerInfo.wmsInfo.forecastLayers?.includes(layerInfo.name)) {
                    featureInfoFormat = layerInfo.layerInfo.wmsInfo.forecastLayerInfoFormat;
                }

                const url = layerSource?.getFeatureInfoUrl(
                    event.coordinate,
                    this.map.getView().getResolution(),
                    this.map.getView().getProjection(),
                    Object.assign({ 'INFO_FORMAT': featureInfoFormat }, extra_dimensions)
                );

                if (url) {
                    if (featureInfoFormat === 'text/html') {
                        promises.push(fetch(url)
                            .then(response => {
                                return response.text();
                            })
                            .then(text => {
                                return {
                                    id: uuidv4(),
                                    url: url,
                                    marker: iconUuid,
                                    source: layerInfo.source,
                                    name: layerInfo.displayName,
                                    layerName: layerInfo.name,
                                    forecastDate: forecastDate,
                                    coordinate: event.coordinate,
                                    data: text
                                };
                            }).catch((e) => {
                                console.error("Error fetching feature info from ", url, e);
                                return {
                                    id: uuidv4(),
                                    url: url,
                                    marker: iconUuid,
                                    source: layerInfo.source,
                                    name: layerInfo.displayName,
                                    layerName: layerInfo.name,
                                    forecastDate: forecastDate,
                                    coordinate: event.coordinate, data: '[]', point: null
                                };
                            })
                        );
                    } else {
                        promises.push(fetch(url)
                            .then(response => {
                                return response.json();
                            })
                            .then((json: Record<string, unknown>[]) => {
                                let data, point, notificationpoint, pp_tab;
                                if (!json || json.length === 0) {
                                    // return { url: url, marker: iconUuid, source: layerInfo.source, name: layerInfo.displayName, coordinate: event.coordinate, data: '[]' };
                                    data = '[]';
                                    point = null;
                                } else if (json.length === 1 && json[0]) {
                                    data = json[0]['html'];
                                    pp_tab = json[0]['pp_tab'];
                                    point = json[0]['point'];
                                    notificationpoint = json[0]['notificationpoint'];
                                } else {
                                    console.error("Unexpected feature info response", json, "Taking element at [0] only");
                                    data = json[0]['html'];
                                    pp_tab = json[0]['pp_tab'];
                                    point = json[0]['point'];
                                    notificationpoint = json[0]['notificationpoint'];
                                }
                                return {
                                    id: uuidv4(),
                                    url: url,
                                    marker: iconUuid,
                                    source: layerInfo.source,
                                    name: layerInfo.displayName,
                                    layerName: layerInfo.name,
                                    forecastDate: forecastDate,
                                    coordinate: event.coordinate,
                                    data: data,
                                    point: point,
                                    notificationpoint: notificationpoint,
                                    pp_tab: pp_tab
                                };
                            }).catch((e) => {
                                console.error("Error fetching feature info from ", url, e);
                                return {
                                    id: uuidv4(),
                                    url: url,
                                    marker: iconUuid,
                                    source: layerInfo.source,
                                    name: layerInfo.displayName,
                                    layerName: layerInfo.name,
                                    forecastDate: forecastDate,
                                    coordinate: event.coordinate,
                                    data: '[]',
                                    point: null,
                                    notificationpoint: null,
                                    pp_tab: null
                                };
                            })
                        );
                    }
                }
            }
        }

        return this.finishGetFeatureInfo(promises, event, iconUuid, forecastDate);
    }

    finishGetFeatureInfo(promises: Promise<GetFeatureInfoData>[], event: MapBrowserEvent, iconUuid: number, forecastDate: string): Promise<FeatureInfoStore> {
        if (promises.length === 0) { return Promise.resolve({}) };

        document.body.style.cursor = 'wait';
        return Promise.all(promises).then((donePromise) => {
            const iconText = `${this.featureCounter}`;

            const iconFeature = new Feature({
                geometry: new Point(event.coordinate),
                uuid: iconUuid,
            });

            let newFeatureAdded = false;

            donePromise.forEach((featurePromise) => {
                const featureData = featurePromise.data;
                // when data is null or not a string, that feature should be dropped
                const hasNoContent = featureData == null || (typeof featureData === 'string'
                    && (featureData.includes('No content') || featureData.includes("No data for layer") || featureData === '[]'))
                    || (featureData instanceof Array && featureData.length === 0);
                if (hasNoContent) {
                    toast(`Layer "${featurePromise.name}" has no data for this point`, { position: "bottom-right", autoClose: 2000, type: "info" });
                    return;
                }
                newFeatureAdded = true;
                this.addFeature(this.featureInfos, iconUuid, featurePromise, event, iconText, iconFeature, forecastDate);
            });

            if (newFeatureAdded && this.useMapMarkers) {
                this.addIcon(iconText, iconFeature);
            }

            return { ...this.featureInfos };
        }).finally(() => {
            document.body.style.cursor = 'default';
        });
    }

    private addIcon(iconText: string, iconFeature: Feature<Point>) {
        const style = new Style({
            image: new Icon({
                anchor: [0.5, 46],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: markerIconFilled,
            }),
            text: new Text({
                text: iconText, offsetX: 0.5, offsetY: -28, font: '20px sans-serif', fill: new Fill({ color: 'white' })
            }),
        });
        iconFeature.setStyle(style);
        this.iconLayer.getSource()?.addFeature(iconFeature);
    }

    loadFeatures(featureInfos: FeatureInfoStore) {
        console.debug("Loading features", featureInfos);
        const featureEntries = Object.entries(featureInfos);
        let highestUuid = 0;
        for (const feature of featureEntries) {
            const iconUuid = feature[0];
            if (parseInt(iconUuid) > highestUuid) {
                highestUuid = parseInt(iconUuid);
            }
            const iconFeature = new Feature({
                geometry: new Point(feature[1].coordinate),
                uuid: iconUuid,
            });
            this.addIcon(iconUuid, iconFeature);
        }
        this.featureCounter = highestUuid + 1;
    }

    private addFeature(featureInfos: FeatureInfoStore, iconUuid: number, featureInfoData: GetFeatureInfoData, evt: MapBrowserEvent, iconText: string, iconFeature: Feature<Point>, forecastDate: string) {
        if (iconUuid in featureInfos) {
            featureInfos[iconUuid].features.push(featureInfoData);
        } else {
            featureInfos[iconUuid] = {
                features: [featureInfoData],
                coordinate: evt.coordinate,
                name: `Feature ${iconText}, Forecast ${forecastDate}`,
                icon: iconFeature
            };
        }
        this.featureCounter += 1;
    }

    removeAllFeatureInfos() {
        const src = this.iconLayer.getSource();
        if (!src) return {};
        src.removeFeatures(src.getFeatures());
        this.featureInfos = {};
        this.featureCounter = 1;
        return { ...this.featureInfos };
    }

    removeFeatureInfo(id: number) {
        const src = this.iconLayer.getSource();
        if (src) {
            src.getFeatures().forEach((feature) => {
                if (feature.get("uuid") === id) {
                    src.removeFeature(feature);
                }
            });
        }
        delete this.featureInfos[id];
        return { ...this.featureInfos };
    };

    zoomToPoint(point: { lat: number, lon: number }) {
        const view = this.map.getView();
        view.setCenter(epsg3857(point));
        view.setZoom(10);
    }

    zoomToFeature(fisd: FeatureInfoStoreData, featureinfoLeftOffset?: React.RefObject<HTMLDivElement | null>) {
        if (!featureinfoLeftOffset || !featureinfoLeftOffset.current || !featureinfoLeftOffset.current.children[0]) { return };
        // const offset = parseInt(featureinfoLeftOffset.current.dataset.offsetx as string);
        const offset = featureinfoLeftOffset.current ? featureinfoLeftOffset.current.children[0].getBoundingClientRect().left : 0;
        let newX = window.innerWidth * 0.75;
        if (featureinfoLeftOffset && offset > (window.innerWidth * 0.3)) {
            newX = window.innerWidth * 0.25;
        }
        const normal = new Icon({
            anchor: [0.5, 46],
            anchorXUnits: 'fraction',
            anchorYUnits: 'pixels',
            src: markerIconFilled
        });
        const selected = new Icon({
            anchor: [0.5, 46],
            anchorXUnits: 'fraction',
            anchorYUnits: 'pixels',
            src: markerIconFilled,
            color: 'orange'
        });

        this.iconLayer.getSource()?.getFeatures().forEach((feature) => {
            const style = (feature.getStyle() as Style);
            style.setImage(normal);
            feature.setStyle(style);
        });
        this.iconLayer.getSource()?.getFeaturesAtCoordinate(fisd.coordinate).forEach((feature) => {
            // console.debug(feature);
            const style = (feature.getStyle() as Style);
            style.setImage(selected);
            feature.setStyle(style);
        })
        this.map.getView().centerOn(fisd.coordinate, this.map.getSize(), [newX, window.innerHeight / 2]);
    }

    setLayerOrder(layerInfo: MapActiveLayer, requestedZIndex: number) {
        const oldZIndex = layerInfo.layer.getZIndex()!;
        const newZIndex = 100 - requestedZIndex;
        console.debug("Reordering layer", layerInfo.id, "from", oldZIndex, "to", newZIndex);

        if (oldZIndex === newZIndex) return [...this.layers];

        const movingUp = newZIndex > oldZIndex;

        for (const currentLayerInfo of this.layers) {
            const z = currentLayerInfo.layer.getZIndex()!;
            if (currentLayerInfo.id === layerInfo.id) {
                currentLayerInfo.layer.setZIndex(newZIndex);
                currentLayerInfo.order = newZIndex;
            } else if (movingUp && z > oldZIndex && z <= newZIndex) {
                currentLayerInfo.layer.setZIndex(z - 1);
                currentLayerInfo.order = z - 1;
            } else if (!movingUp && z < oldZIndex && z >= newZIndex) {
                currentLayerInfo.layer.setZIndex(z + 1);
                currentLayerInfo.order = z + 1;
            }
        }
        return [...this.layers.sort((a, b) => b.order - a.order)];
    }
    reorderLayer(layerInfo: MapActiveLayer, zIndexChange: number) {
        const oldZIndex = layerInfo.layer.getZIndex()!;
        const nn = oldZIndex + zIndexChange;
        console.debug("Reordering layer", layerInfo.id, "from", oldZIndex, "to", nn);
        const newZIndex = Math.max(Math.min(nn, 100), 100 - this.layers.length + 1);

        for (const currentLayerInfo of this.layers) {
            if (currentLayerInfo.layer.getZIndex() == oldZIndex) {
                currentLayerInfo.layer.setZIndex(newZIndex);
                currentLayerInfo.order = newZIndex;
                continue;
            } else if (currentLayerInfo.layer.getZIndex() == newZIndex) {
                currentLayerInfo.layer.setZIndex(oldZIndex);
                currentLayerInfo.order = oldZIndex;
                continue;
            }
        }
        return [...this.layers.sort((a, b) => b.order - a.order)];
    }

    sendLayerToTop(layerInfo: MapActiveLayer) {
        const oldZIndex = layerInfo.layer.getZIndex()!;
        const topZIndex = 100;

        console.debug("Sending layer to top", layerInfo.id, "from", oldZIndex, "to", topZIndex);

        // If already at top, do nothing
        if (oldZIndex === topZIndex) {
            return [...this.layers];
        }

        // Move the selected layer to the top (zIndex 100)
        layerInfo.layer.setZIndex(topZIndex);
        layerInfo.order = topZIndex;

        // Shift down all layers that were above the selected layer
        for (const currentLayerInfo of this.layers) {
            if (currentLayerInfo.id === layerInfo.id) {
                continue;
            }
            const currentZIndex = currentLayerInfo.layer.getZIndex()!;
            if (currentZIndex > oldZIndex) {
                const newZIndex = currentZIndex - 1;
                currentLayerInfo.layer.setZIndex(newZIndex);
                currentLayerInfo.order = newZIndex;
            }
        }

        return [...this.layers.sort((a, b) => b.order - a.order)];
    }

    // addFeaturesAsLayer(features: any[], layerName: string) {
    //     const vectorSource = new VectorSource({
    //         features: (new GeoJSON()).readFeatures({ "type": "FeatureCollection", "features": features })
    //     });
    //     const vectorLayer = new VectorLayer({
    //         source: vectorSource,
    //         style: {
    //             'stroke-color': 'rgba(100, 255, 0, 1)',
    //             'stroke-width': 2,
    //             'fill-color': 'rgba(100, 255, 0, 0.3)',
    //         },
    //     });
    //     this.map.addLayer(vectorLayer);
    //     this.layers[layerName] = vectorLayer;
    // }

    // addFeaturesAsLayer(data: any, layerName: string) {
    //     const vectorSource = new VectorSource({
    //         features: (new GeoJSON({
    //             dataProjection: 'EPSG:4326',
    //             featureProjection: 'EPSG:3857',
    //         }
    //         )).readFeatures({ "type": "FeatureCollection", "features": data })
    //     });
    //     const vectorLayer = new VectorLayer({
    //         source: vectorSource,
    //         style: {
    //             'fill-color': ['get', 'impact_p90_colour'],
    //             'stroke-color': 'lightgrey',
    //             'stroke-width': 0.5,
    //         }
    //         //  [
    //         //     {
    //         //         filter: ['==', ['get', 'impact_p90'], 3],
    //         //         style: {
    //         //             'fill-color': 'rgba(255, 0, 0, 0.3)',
    //         //             'stroke-color': 'rgba(255, 0, 0, 0.9)',
    //         //             'stroke-width': 0.5,
    //         //         }
    //         //     },
    //         //     {
    //         //         else: true,
    //         //         filter: ['==', ['get', 'impact_p90'], 2],
    //         //         style: {
    //         //             'fill-color': 'orange',
    //         //             'stroke-color': 'orange',
    //         //             'stroke-width': 0.5,
    //         //         }
    //         //     }
    //         // ]
    //         // {
    //         // "fill-color": [
    //         //     "case",
    //         //     // ["==", ["get", "impact_p90"], 4], "purple",
    //         //     ["==", ["get", "impact_p90"], 3], "red",
    //         //     // ["==", ["get", "impact_p90"], 2], "yellow",
    //         //     [">", ["get", "impact_p90"], 1], "green",
    //         // ],

    //         // },
    //     });
    //     this.map.addLayer(vectorLayer);
    //     this.layers[layerName].layer = vectorLayer;
    // }

    /**
     * Create a layer name for the WMS and layer combination.
     * @param wmsName URL of the WMS server
     * @param name Name of the layer
     * @returns Generated string for the combination of the url and layer name
     */
    makeStorageName(wmsName: string, group: string, name: string) {
        return `${wmsName}${LAYER_ENCODE_SEPARATOR}${group}${LAYER_ENCODE_SEPARATOR}${name}`;
    }

    async addOrRemoveLayer(wmsName: string, group: string, layer: WMSLayer, forecastDate: string) {
        const name = layer.Name;
        const layerStorageName = this.makeStorageName(wmsName, group, name);
        const removeResult = this.removeLayer(layerStorageName);
        this.layers = removeResult.newLayers;

        if (removeResult.removed) {
            return this.layers;
        }
        else {
            return this.addLayer(wmsName, group, layer, forecastDate);
        }

    }
    addLayer(wmsName: string, group: string, layer: WMSLayer, forecastDate: string, visible?: boolean, opacity?: number) {
        const name = layer.Name;
        const layerStorageName = this.makeStorageName(wmsName, group, name);
        const removeResult = this.removeLayer(layerStorageName);
        this.layers = removeResult.newLayers;
        const order = 100 - this.layers.length;
        console.debug("Adding layer", name, "from", wmsName, "with order", order);
        let olLayer: TileLayer<TileWMS> | VectorTileLayer | VectorLayer | ImageLayer<ImageStatic> | ImageLayer<ImageWMS>;

        if (name.includes("VectorTile")) {
            olLayer = this.makeVectorTileLayer(wmsName, name, forecastDate, order);
        } else if (name.includes("Vector")) {
            olLayer = this.makeVectorLayer(wmsName, name, forecastDate, order);
            const vectorName = name.replace("mapserver:", "");
            fetch(`/api/fms/style/efas/${forecastDate}/${vectorName}`)
                .then((response) => response.json())
                .then((styleJson) => {
                    (olLayer as VectorLayer).setStyle(styleJson);
                }).catch((e) => {
                    console.error("Error fetching style for vector layer", vectorName, e);
                });

        } else if (layer.Keywords && layer.Keywords['layer_data'] === 'imagestatic') {
            olLayer = this.makeImageStaticLayer(wmsName, layer, forecastDate, order);
        } else if (layer.Keywords && layer.Keywords['layer_data'] === 'imagewms') {
            olLayer = this.makeImageWmsLayer(wmsName, layer, forecastDate, order);
        } else {
            olLayer = this.makeTileWMSLayer(wmsName, layer, forecastDate, order);
        }

        if (visible !== undefined) {
            olLayer.setVisible(visible);
        }

        if (opacity !== undefined && Number.isFinite(opacity)) {
            olLayer.setOpacity(opacity);
        }

        this.map.addLayer(olLayer);

        // update the local reference even though it'll be updated in useEffect in the map
        // this allows the internal state to be up to date even before the current render cycle has finished
        // e.g. when initially loading the active layers after reading them from the URL
        this.layers.push({
            id: layerStorageName,
            source: wmsName,
            name: name,
            group: group,
            displayName: layer.Title,
            order: order,
            layer: olLayer,
            layerInfo: layer,
            dimensions: layer.Dimension
        });
        return this.layers;
    }

    toggleLayer(id: string, visible?: boolean) {
        for (const layer of this.layers) {
            if (layer.id === id) {
                layer.layer.setVisible(visible ?? !layer.layer.getVisible());
            }
        }
        return [...this.layers];
    }
    setAllLayersVisibility(visible: boolean) {
        for (const layer of this.layers) {
            layer.layer.setVisible(visible);
        }
        return [...this.layers];
    }
    removeLayer(id: string) {
        if (this.layers.length === 0) return { newLayers: [], removed: false };
        const newLayers: MapActiveLayer[] = [];
        let removedLayerOrder: number | null = null;

        let removed = false;

        for (const layerInfo of this.layers) {
            if (layerInfo.id === id) {
                if (layerInfo.layer.getSource() instanceof ImageStatic) {
                    this.imageStaticCache = new globalThis.Map<string, ImageStatic>();
                }
                this.map.removeLayer(layerInfo.layer);
                removedLayerOrder = layerInfo.order;
                removed = true;
            } else {
                // adjust the zindex/order for layers after the removed one by 1
                if (removedLayerOrder !== null && layerInfo.order < removedLayerOrder) {
                    console.debug('reducing order of layer', layerInfo.id, 'from', layerInfo.order, 'to', layerInfo.order + 1);

                    layerInfo.order = layerInfo.order + 1;
                    layerInfo.layer.setZIndex(layerInfo.order);
                }

                newLayers.push(layerInfo);
            }
        }
        return { newLayers: newLayers, removed: removed };
    }

    removeAllLayers() {
        for (const layerInfo of this.layers) {
            this.map.removeLayer(layerInfo.layer);
        }
        this.imageStaticCache = new globalThis.Map<string, ImageStatic>();
        this.layers = [];
        return this.layers;
    }

    setForecastDate(forecastDate: string) {
        this.setForecastDateToLayers(this.layers, forecastDate);
    }

    setForecastDateToLayers(layers: MapActiveLayer[], forecastDate: string) {
        for (const layerInfo of layers) {
            if (layerInfo.layer) {
                const layerSource = layerInfo.layer.getSource();
                if (layerSource instanceof TileWMS) {
                    if (this.hasDimension(layerInfo.dimensions, 'time')) {
                        layerSource.updateParams({ 'TIME': forecastDate });
                    }
                    // workaround for layers that have a reftime and use the TIME as the step
                    // since that's how the old UI is doing it
                    if (this.hasDimension(layerInfo.dimensions, 'reftime')) {
                        layerSource.updateParams({ 'reftime': forecastDate });
                    }
                } else {
                    this.updateDimension(layerInfo, 'TIME', forecastDate);
                }
            }
        }
    }
    makeImageStaticLayer(wmsName: string, layer: WMSLayer, forecastDate: string, zIndex: number) {
        const layerForecastPath = moment(forecastDate).format('YYYY/YYYYMM/YYYYMMDDHH');
        const layerSourceUrl = layer.Keywords['layer_source'];

        const staticRaster = new ImageLayer({
            source: new ImageStatic({
                url: `/api/fms/raster/europe5km/${layerForecastPath}/${layerSourceUrl?.replace("{step_zfill_3}", "000")}`,
                // projection: 'EPSG:3857',
                // imageExtent: [-2809889.4801068804, 2600807.342657552, 5594732.0747852735, 11840812.256012306],
                interpolate: false,
                projection: 'EPSG:4326',
                imageExtent: [-25.241666666666667, 22.74166666666666, 50.258333333333333, 72.24166666666666],
            }),
            opacity: 0.8,
            zIndex: zIndex
        });
        return staticRaster;
    }
    makeImageWmsLayer(wmsName: string, layer: WMSLayer, forecastDate: string, zIndex: number) {
        const params: { LAYERS: string, TILED: boolean, VERSION: string, TIME?: string, reftime?: string, force_mapserver?: string } = { LAYERS: layer.Name, TILED: true, VERSION: layer.wmsInfo.version };

        if (forecastDate) {
            if (this.hasDimension(layer.Dimension, 'time')) {
                params['TIME'] = forecastDate;
            }
            if (this.hasDimension(layer.Dimension, 'reftime')) {
                params['reftime'] = forecastDate;
            }
        }
        if (this.forceMapserver) {
            params['force_mapserver'] = '1';
        }

        const nlSrc = new ImageWMS({
            url: wmsName,
            params: params,
        });

        return new ImageLayer({
            source: nlSrc,
            opacity: 0.8,
            zIndex: zIndex
        });
    }
    makeTileWMSLayer(wmsName: string, layer: WMSLayer, forecastDate: string, zIndex: number) {
        const params: { LAYERS: string, TILED: boolean, VERSION: string, TIME?: string, reftime?: string, force_mapserver?: string } = { LAYERS: layer.Name, TILED: true, VERSION: layer.wmsInfo.version };

        if (forecastDate) {
            if (this.hasDimension(layer.Dimension, 'time')) {
                params['TIME'] = forecastDate;
            }
            if (this.hasDimension(layer.Dimension, 'reftime')) {
                params['reftime'] = forecastDate;
            }
        }
        if (this.forceMapserver) {
            params['force_mapserver'] = '1';
        }

        // https://openlayers.org/en/latest/examples/wms-custom-tilegrid-512x256.html
        const tileGrid = buildTileGrid(layer.wmsInfo.tileSize);

        const nlSrc = new TileWMS({
            url: wmsName,
            params: params,
            transition: 0.5,
            gutter: layer.wmsInfo.gutter ?? (layer.Keywords.wms_gutter ? parseInt(layer.Keywords.wms_gutter) : undefined) ?? 32,
            wrapX: false,
            tileGrid: tileGrid,
        });

        return new TileLayer({
            source: nlSrc,
            opacity: 0.8,
            // preload: 8,
            zIndex: zIndex
        });
    }

    makeVectorLayer(wmsName: string, name: string, forecastDate: string, zIndex: number) {
        const url = new URL(wmsName);
        const vectorName = name.replace("mapserver:", "");

        // split the request of the vector data into 6 parts (hard-coded 0-5)
        // send the requests in parallel and combine the features into one source
        // the url where the backend supports this is url: `${url.origin}/api/fms/vector-partial/${forecastDate}/${vectorName}/${part}`,
        if (name.includes("VectorPartial")) {
            const vectorSource = new VectorSource({});
            const parts = 48;
            for (let part = 0; part < parts; part++) {
                fetch(`${url.origin}/api/fms/vector-partial/${forecastDate}/${vectorName}/${part}/${parts}`)
                    .then((response) => response.json())
                    .then((json: {
                        crs: { type: string, properties: { name: string } },
                        type: string,
                        features: unknown[]
                    }) => {
                        const features = (new GeoJSON({
                            dataProjection: 'EPSG:4326',
                            featureProjection: 'EPSG:3857',
                        })).readFeatures(json);

                        vectorSource.addFeatures(features);
                        return features;
                    })
            }

            return new VectorLayer({
                source: vectorSource,
                zIndex: zIndex
            });
        } else {

            // Vector Reporting Points
            // could also pre-load the geojson for the features and keep it ready to go for the users
            return new VectorLayer({
                source: new VectorSource({
                    // features: features
                    format: new GeoJSON(),
                    url: `${url.origin}/api/fms/vector/${forecastDate}/${vectorName}`,
                }),
                zIndex: zIndex
            });
        }

    }

    makeVectorTileLayer(wmsName: string, name: string, forecastDate: string, zIndex: number) {
        const styleSeasonalBasins = (feature: FeatureLike, _res: number) => {
            console.debug(feature);
            return new Style(
                {
                    fill: new Fill({
                        color: feature.get('colour')
                    }),
                    stroke: new Stroke({
                        color: 'black',
                        width: 0.5
                    }),
                }
            );
        }

        const url = new URL(wmsName);

        // Vector Reporting Points
        // could also pre-load the geojson for the features and keep it ready to go for the users
        // const features = (new GeoJSON()).readFeatures(reportingPoints);
        // return new VectorLayer({
        //     source: new VectorSource({
        //         // features: features
        //         format: new GeoJSON(),
        //         url: `${url.origin}/api/fms/vector/${forecastDate}/RepPoints`,
        //     }),
        //     style: styleCB,
        //     zIndex: Object.keys(this.layers).length
        // });

        return new VectorTileLayer({
            declutter: true,
            source: new VectorTileSource({
                format: new MVT(
                    { layerName: "RepPoints", idProperty: 'ObsID' }
                ),
                url: `${url.origin}/api/fms/vectortile/${forecastDate}/RepPoints/{z}/{x}/{y}.pbf`,
                // projection: 'EPSG4326',
                // tileLoadFunction: function (tile, url) {
                //     console.debug("Loading tiles");
                //     tile.setLoader(function (extent, resolution, projection) {
                //         fetch(url).then(function (response) {
                //             response.arrayBuffer().then(function (data) {
                //                 const format = tile.getFormat() // ol/format/MVT configured as source format
                //                 const features = format.readFeatures(data, {
                //                     extent: extent,
                //                     featureProjection: projection
                //                 });
                //                 tile.setFeatures(features);
                //             });
                //         });
                //     });
                // }
            }),
            style: styleSeasonalBasins,
            zIndex: zIndex
        });
    }

    showLegend(layerInfo: MapActiveLayer, forecastDate: string): Promise<LegendData> {
        // const url = new URL(layerInfo.source);
        return fetch(`/api/fms/${IS_EFAS ? "legends" : "legends-v2/glofas"}/${layerInfo.name}/${forecastDate.split("T")[0]}-${forecastDate.split("T")[1].split(":")[0]}`)
            .then((response) => response.json())
            .then((json: LegendData) => {
                this.legend = json;
                return this.legend;
            });
    }

    changeOpacity(layerInfo: MapActiveLayer, opacity: number) {
        layerInfo.layer.setOpacity(opacity);
    }

    updateDimension(layerInfo: MapActiveLayer, dimension: string, value: string, forecastDate?: string) {
        console.debug("Updating dimension", dimension, "to", value, "for layer", layerInfo.name, 'forecastDate:', forecastDate);
        const layer = layerInfo.layer;
        if (layer instanceof TileLayer) {
            const layerSource = layer.getSource();
            if (layerSource instanceof TileWMS) {
                if (this.hasDimension(layerInfo.dimensions, dimension)) {
                    layerSource.updateParams({ [dimension]: value });
                } else {
                    console.warn(`Dimension ${dimension} not found in layer ${layerInfo.name}`);
                }
            }
        } else if (layer instanceof VectorLayer) {
            const layerSource = layer.getSource();
            if (layerSource instanceof VectorSource) {
                const vectorLayerName = layerInfo.name.replace("mapserver:", "");
                if (this.hasDimension(layerInfo.dimensions, dimension)) {
                    if (forecastDate) {
                        layerSource.setUrl(`/api/fms/vector/${forecastDate}/${value}/${vectorLayerName}`);
                    } else {
                        layerSource.setUrl(`/api/fms/vector/${value}/${vectorLayerName}`);
                    }
                    layerSource.refresh();
                } else {
                    console.warn(`Dimension ${dimension} not found in layer ${vectorLayerName}`);
                }
            }
        } else if (layer instanceof ImageLayer) {
            const layerSource = layer.getSource();
            if (layerSource instanceof ImageStatic) {
                // if only value is provided then that is step 0
                // otherwise we need to calculate the forecast path from the forecast date
                let valueToUse = value, step = "000";
                if (forecastDate) {
                    valueToUse = forecastDate;
                    // step is the hours difference between forecastDate and value
                    const fd = moment(forecastDate);
                    const vd = moment(value);
                    step = vd.diff(fd, 'hours').toString().padStart(3, '0');
                    console.debug("Calculated step", step, "from forecastDate", forecastDate, "and value", value);
                }
                const layerForecastPath = moment(valueToUse).format('YYYY/YYYYMM/YYYYMMDDHH');
                const layerSourceUrl = layerInfo.layerInfo.Keywords['layer_source'];
                const imageUrl = `/api/fms/raster/europe5km/${layerForecastPath}/${layerSourceUrl?.replace("{step_zfill_3}", step)}`;

                // Check cache first to avoid recreating sources for already visited steps
                let source = this.imageStaticCache.get(imageUrl);
                if (!source) {
                    source = new ImageStatic({
                        url: imageUrl,
                        projection: 'EPSG:4326',
                        imageExtent: [-25.241666666666667, 22.74166666666666, 50.258333333333333, 72.24166666666666]
                    });
                    this.imageStaticCache.set(imageUrl, source);
                }
                layer.setSource(source);
            } else if (layerSource instanceof ImageWMS) {
                if (this.hasDimension(layerInfo.dimensions, dimension)) {
                    layerSource.updateParams({ [dimension]: value });
                } else {
                    console.warn(`Dimension ${dimension} not found in layer ${layerInfo.name}`);
                }
            }
        }
    }

    addToCart(gifd: GetFeatureInfoData) {
        // tack on the feature info URL otherwise we can't retrieve it later outside the mapviewer
        // and the cart allows looking at it on demand
        const notificationPoint = gifd.notificationpoint as { featureInfoURL?: string };
        notificationPoint.featureInfoURL = gifd.url;
        return fetch(`/api/settings/carts-v2/?format=json`, {
            method: "POST",
            body: JSON.stringify({ point: notificationPoint }),
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            }
        });
    }

    setBaseMap(map: number) {
        this.baseMapLayer.setVisible(true);
        const src = this.baseMapLayer.getSource();
        if (!src) { return; }
        switch (map) {
            case 2:
                src.setUrl('https://gisco-services.ec.europa.eu/maps/tiles/Continents/EPSG3857/{z}/{x}/{y}.png');
                break;
            case 3:
                src.setUrl('https://gisco-services.ec.europa.eu/maps/tiles/OSMBrightCompositeEN/EPSG3857/{z}/{x}/{y}.png');
                break;
            case 4:
                src.setUrl('https://gisco-services.ec.europa.eu/maps/tiles/OSMCartoHDMCompositeEN/EPSG3857/{z}/{x}/{y}.png');
                break;
            case 5:
                src.setUrl('https://api.maptiler.com/maps/4fb4b2bb-8cc9-486c-ace6-ebf229edd55c/{z}/{x}/{y}.png?key=51hIFdsn8DZSgRZKW5AU');
                break;
            case 6:
                src.setUrl('https://api.maptiler.com/maps/a3cb6961-daa6-4e98-9508-1ff559fb3cca/{z}/{x}/{y}.jpg?key=51hIFdsn8DZSgRZKW5AU');
                break;
            case 7:
                src.setUrl('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}.png');
                break;
            case 8:
                this.baseMapLayer.setVisible(false);
                break;
            default:
            case 1:
                src.setUrl('https://gisco-services.ec.europa.eu/maps/tiles/OSMCartoV4CompositeEN/EPSG3857/{z}/{x}/{y}.png');
                break
        }
    }
    setBaseMapOpacity(value: number) {
        this.baseMapLayer.setOpacity(value);
    }

    async showActiveNotifications(date: string, latestForecast?: string) {
        const mDate = moment.utc(date);
        const isRecent = Math.abs(moment.utc().diff(mDate, 'days')) <= 2;
        const dateParam = !latestForecast || mDate.isSame(moment.utc(latestForecast)) ? '' : `&date=${date}`;
        const url = `/api/notifications/alert-points/?format=json&active=true${dateParam}${isRecent ? `&time=${Date.now()}` : ''}`;
        return fetch(url, {
            method: "GET",
            credentials: "include",
            ...(isRecent ? { cache: "no-store" } : {}),
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            }
        }).then((response) => {
            if (response.status === 200) {
                return response.json();
            } else {
                toast.error("Error fetching active notifications, please try again");
            }
        }).then((json: AlertPoint[]) => {
            if (json && json.length > 0) {
                this.renderNotificationIcons(json);
            }
            return json;
        }).catch((error) => {
            console.error("Error fetching active notifications", error);
            toast.error("Error fetching active notifications, please try again");
            return [];
        });
    }

    renderNotificationIcons(points: AlertPoint[]) {
        this.activeNotificationsLayer.getSource()?.clear();

        if (!points || points.length === 0) {
            this.activeNotificationsLayer.setVisible(false);
            return;
        }

        const formalIconStyle = new Style({
            image: new Icon({
                anchor: [0.51, 80],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: formalIcon,
            }),
        });

        const informalIconStyle = new Style({
            image: new Icon({
                anchor: [0.51, 80],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: informalIcon,
            }),
        });

        const flashFloodIconStyle = new Style({
            image: new Icon({
                anchor: [0.51, 80],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: flashFloodIcon,
            }),
        });

        const iconSource = this.activeNotificationsLayer.getSource();
        for (const alertPoint of points) {
            const feature = new Feature({
                geometry: new Point(fromLonLat([alertPoint.location.coordinates[0], alertPoint.location.coordinates[1]], "EPSG:3857")),
                name: "active-notification",
            });
            feature.setStyle(alertPoint.alert_type === 1 ? formalIconStyle : alertPoint.alert_type === 2 ? informalIconStyle : flashFloodIconStyle);
            feature.setProperties(alertPoint);
            iconSource!.addFeature(feature);
        }

        this.activeNotificationsLayer.setVisible(true);
    }

    clearActiveNotifications() {
        this.activeNotificationsLayer.getSource()?.clear();
        this.activeNotificationsLayer.setVisible(false);
        this.clearInteractivePopup();
    }

    showInteractivePopup(event: MapBrowserEvent, popupElement: HTMLDivElement) {
        return this.interactivePopupService.findFeatureAndCreateOverlay(
            this.map,
            event,
            popupElement,
            this.activeNotificationsLayer,
            this.missedEvents.getMissedEventsLayer(),
            this.feedbacks.getFeedbacksLayer()
        );
    }

    clearInteractivePopup() {
        this.interactivePopupService.clearPopup(this.map);
    }

    hitIconLayer(event: MapBrowserEvent): boolean {
        const hit = this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
            if (layer === this.iconLayer) {
                const featureId = feature.get("uuid");
                if (featureId) {
                    this.removeFeatureInfo(featureId);
                    return true;
                }
            }
            return false;
        }, {
            hitTolerance: 5
        });
        return !!hit;
    }

    forecastIsWithinEmbargo(forecastDate: string, latestForecastDate: string) {
        if (!IS_EFAS) {
            return false;
        }
        // if the forecast is within 30 days of the latest forecast then the embargo is ON
        const embargoOn = moment.utc(latestForecastDate).diff(moment.utc(forecastDate), 'days') < 30;

        return embargoOn;
    }
}
